import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Column } from '@odin-labs/components';
import {
  GetAccessGroupMembersQuery,
  useGetAccessGroupMembersLazyQuery,
  useGetAccessGroupMembersQuery,
} from 'apollo/generated/client-operations';
import {
  actorName,
  formatAccessDate,
  membershipSourceLabels,
} from './presentation';
import { AccessGroupsTable } from './AccessGroupsTable';
import { AccessGroupsError } from './AccessGroupStatus';

type Member = NonNullable<
  GetAccessGroupMembersQuery['accessGroup']
>['members'][number];

const getMemberColumns = (timeZone: string): Column<Member>[] => [
  {
    id: 'worker',
    Header: 'Worker',
    accessor: (member) =>
      member.worker ? actorName(member.worker.user) : 'Restricted worker',
    Cell: ({ row }): React.ReactElement => {
      const { worker } = row.original;
      // The viewer may not read workers of other contractors at a shared site.
      if (!worker) return <span>Restricted worker</span>;
      return (
        <Link to={`/worker/${worker.workerId}/access-groups`}>
          {actorName(worker.user)}
        </Link>
      );
    },
  },
  {
    id: 'granted',
    Header: 'Granted',
    accessor: (member) =>
      `${formatAccessDate(member.grantedAt, timeZone)} · ${actorName(
        member.grantedBy,
      )} (${membershipSourceLabels[member.grantSource]})`,
  },
];

/**
 * Members load a page at a time and only when this mounts or `version`
 * changes, so editing the group's zones or name never reloads every member.
 */
export function AccessGroupMembers({
  siteId,
  groupId,
  memberCount,
  timeZone,
  version,
}: {
  siteId: string;
  groupId: string;
  memberCount: number;
  timeZone: string;
  version: number;
}): React.ReactElement {
  const variables = { siteId, id: groupId };
  const { data, loading, error, refetch } = useGetAccessGroupMembersQuery({
    variables,
    fetchPolicy: 'no-cache',
  });
  const [loadPage, next] = useGetAccessGroupMembersLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [more, setMore] = React.useState<Member[]>([]);
  // memberCount comes from the details query and can be stale; an empty page
  // means there is nothing more to load.
  const [ended, setEnded] = React.useState(false);
  const first = React.useRef(true);
  React.useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setMore([]);
    setEnded(false);
    refetch().catch((): undefined => undefined);
  }, [version, refetch]);
  const columns = React.useMemo(() => getMemberColumns(timeZone), [timeZone]);
  const members = [...(data?.accessGroup?.members ?? []), ...more];
  const showMore = async (): Promise<void> => {
    const last = members[members.length - 1];
    const result = await loadPage({
      variables: { ...variables, after: last?.cursor },
    });
    const page = result.data?.accessGroup?.members ?? [];
    if (!page.length) setEnded(true);
    setMore((loaded) => [...loaded, ...page]);
  };
  if (error) return <AccessGroupsError retry={(): unknown => refetch()} />;
  return (
    <>
      <AccessGroupsTable
        enablePagination
        columns={columns}
        data={members}
        noResultsText="No active members"
        disableGlobalFilter
      />
      {members.length < memberCount && !ended && !loading && (
        <Button
          size="sm"
          theme="secondary"
          text={`Show more members (${members.length} of ${memberCount})`}
          disabled={next.loading}
          onClick={(): unknown => showMore()}
        />
      )}
    </>
  );
}
